import {
  CountryConfig,
  DEFAULT_COUNTRY_CONFIG,
  getCountryConfig,
} from '../services/countryConfigService'

/** True when Settings reports Saudi Arabia / SAR. */
export const isSaudiCountry = (
  config: CountryConfig = DEFAULT_COUNTRY_CONFIG
): boolean =>
  config.country.code === 'SA' || config.currency === 'SAR'

/** True when Settings reports Egypt / EGP. */
export const isEgyptCountry = (
  config: CountryConfig = DEFAULT_COUNTRY_CONFIG
): boolean =>
  config.country.code === 'EG' || config.currency === 'EGP'

/**
 * Active phone region from Settings.
 * Empty/unset → SA (preserves existing Saudi validation).
 */
export const getActivePhoneRegion = (
  config: CountryConfig = getCountryConfig()
): 'SA' | 'EG' | string => {
  if (isEgyptCountry(config)) return 'EG'
  if (config.phoneRegion) return config.phoneRegion
  return 'SA'
}

export interface PhoneInputConfig {
  minLength: number
  maxLength: number
  startsWith?: string
}

/** Phone rules aligned with Besalla-Mobile (EG: 10 starting 1; SA: 9 starting 5). */
export const getPhoneInputConfig = (
  phoneRegion?: string
): PhoneInputConfig => {
  const region = (phoneRegion || getActivePhoneRegion()).toUpperCase()
  if (region === 'EG') {
    return { minLength: 10, maxLength: 11, startsWith: '1' }
  }
  // Existing Saudi behavior
  return { minLength: 9, maxLength: 12, startsWith: '5' }
}

export const sanitizePhoneNumber = (
  value: string,
  phoneRegion?: string
): string =>
  value.replace(/\D/g, '').slice(0, getPhoneInputConfig(phoneRegion).maxLength)

/**
 * Validate national phone digits for active country.
 * Egypt: 10 digits starting with 1, or 11 starting with 01.
 * Saudi: 9 digits starting with 5.
 */
export const isValidPhoneForRegion = (
  phone: string,
  phoneRegion?: string
): boolean => {
  const digits = phone.replace(/\D/g, '')
  const region = (phoneRegion || getActivePhoneRegion()).toUpperCase()

  if (region === 'EG') {
    if (digits.length === 10 && digits.startsWith('1')) return true
    if (digits.length === 11 && digits.startsWith('01')) return true
    return false
  }

  // Existing Saudi national number: 9 digits starting with 5
  return digits.length === 9 && digits.startsWith('5')
}

/**
 * Text currency label for non-icon contexts.
 * Egypt → localized ج.م / EGP; otherwise keep existing Saudi text label.
 */
export const getCurrencyTextLabel = (
  config: CountryConfig,
  egyptSymbol: string,
  saudiLabel: string
): string => (isEgyptCountry(config) ? egyptSymbol : saudiLabel)
